import { useRef, useCallback, useEffect } from 'react';
import { normalizeRichHtml } from '@components/common/RichEditor';

/**
 * 问卷题干 RichEditor：输入后 500ms 防抖保存，blur 立即保存；与上次成功落库内容一致则跳过，避免重复请求。
 * blur 与防抖合并：blur 会清掉定时器再保存一次，不会与待触发的防抖各保存一次。
 */
export function useQuestionStemAutosave({ debounceMs = 500, itemId, remoteBody, onPersist }) {
    const timerRef = useRef(null);
    const lastOkRef = useRef(normalizeRichHtml(remoteBody ?? null));
    const persistGenRef = useRef(0);

    useEffect(() => {
        lastOkRef.current = normalizeRichHtml(remoteBody ?? null);
    }, [itemId, remoteBody]);

    const persist = useCallback(
        async nextBodyRaw => {
            const nextBody = normalizeRichHtml(nextBodyRaw ?? '');
            if (lastOkRef.current === nextBody) {
                return;
            }
            const gen = ++persistGenRef.current;
            const ok = await onPersist(nextBody);
            if (gen !== persistGenRef.current) {
                return;
            }
            if (ok) {
                lastOkRef.current = nextBody;
            }
        },
        [onPersist],
    );

    const schedule = useCallback(
        nextBodyRaw => {
            const nextBody = normalizeRichHtml(nextBodyRaw ?? '');
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                void persist(nextBody);
            }, debounceMs);
        },
        [persist, debounceMs],
    );

    const flush = useCallback(
        nextBodyRaw => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            void persist(nextBodyRaw ?? '');
        },
        [persist],
    );

    useEffect(
        () => () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        },
        [],
    );

    return { schedule, flush };
}
